import React, { useContext } from 'react';

// components and helpers
import { useNavigate } from 'react-router-dom';
import Loader from '../Shared/Loader';
import utils from '../../utils/utils';
import MUIDataTable from 'mui-datatables';

// mui
import Grid from '@mui/material/Grid';
import StarRateIcon from '@mui/icons-material/StarRate';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import BoltIcon from '@mui/icons-material/Bolt';
import BrightnessLowIcon from '@mui/icons-material/BrightnessLow';
import CloudIcon from '@mui/icons-material/Cloud';
import FilterVintageIcon from '@mui/icons-material/FilterVintage';
import FolderIcon from '@mui/icons-material/Folder';
import Button from '@mui/material/Button';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { AppContext } from '../../containers/AppProviderWrapper';
import { Badge } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

interface WorkspaceListProps {
    handleOpenDialog: () => void;
    setOpenPendingInvites: (value: boolean) => void;
}

const WorkspaceList: React.FC<WorkspaceListProps> = ({
    handleOpenDialog,
    setOpenPendingInvites,
}) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { workspaceList, workspaceListLoading } = useContext(AppContext);
    const pendingInvitesList = useSelector(
        (state: any) => state.security.pendingInvitesList
    );

    const handleOpenWorkspace = (value: any) => {
        navigate(`/workspace/${value[0]}`);
    };

    const columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                display: false,
            },
        },
        {
            name: 'icon',
            label: 'Icon',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        const result = iconList.filter(
                            (data) => data?.key === value
                        )[0];
                        return result.value;
                    } else {
                        return (
                            <FolderIcon
                                style={{
                                    color:
                                        theme.palette.mode === 'light'
                                            ? '#663299'
                                            : '#fff',
                                }}
                            />
                        );
                    }
                },
                setCellProps: (value) => {
                    return {
                        style: {
                            width: '5%',
                        },
                    };
                },
            },
        },
        {
            name: 'name',
            label: 'Name',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '---';
                    }
                },
                setCellProps: (value) => {
                    return {
                        style: {
                            width: '25%',
                        },
                    };
                },
            },
        },
        {
            name: 'description',
            label: 'Description',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '---';
                    }
                },
                setCellProps: (value) => {
                    return {
                        style: {
                            width: '22%',
                        },
                    };
                },
            },
        },
        {
            name: 'workspaceRole',
            label: 'Role',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return utils.toTitleText(value.toLowerCase());
                    } else {
                        return 'OWNER';
                    }
                },
                setCellProps: (value) => {
                    return {
                        style: {
                            width: '8%',
                        },
                    };
                },
            },
        },
        {
            name: 'createdBy',
            label: 'Created By',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '---';
                    }
                },
                setCellProps: (value) => {
                    return {
                        style: {
                            width: '23%',
                        },
                    };
                },
            },
        },
        {
            name: 'createdAt',
            label: 'Created At',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return utils.formatDate(value);
                    } else {
                        return '---';
                    }
                },
                setCellProps: (value) => {
                    return {
                        style: {
                            width: '15%',
                        },
                    };
                },
            },
        },
    ];

    const iconList = [
        {
            key: 'star',
            value: (
                <StarRateIcon
                    style={{
                        color:
                            theme.palette.mode === 'light' ? '#663299' : '#fff',
                    }}
                />
            ),
        },
        {
            key: 'heart',
            value: (
                <FavoriteIcon
                    style={{
                        color:
                            theme.palette.mode === 'light' ? '#663299' : '#fff',
                    }}
                />
            ),
        },
        {
            key: 'autoGraph',
            value: (
                <AutoGraphIcon
                    style={{
                        color:
                            theme.palette.mode === 'light' ? '#663299' : '#fff',
                    }}
                />
            ),
        },
        {
            key: 'moon',
            value: (
                <BedtimeIcon
                    style={{
                        color:
                            theme.palette.mode === 'light' ? '#663299' : '#fff',
                    }}
                />
            ),
        },
        {
            key: 'lightning',
            value: (
                <BoltIcon
                    style={{
                        color:
                            theme.palette.mode === 'light' ? '#663299' : '#fff',
                    }}
                />
            ),
        },
        {
            key: 'sun',
            value: (
                <BrightnessLowIcon
                    style={{
                        color:
                            theme.palette.mode === 'light' ? '#663299' : '#fff',
                    }}
                />
            ),
        },
        {
            key: 'cloud',
            value: (
                <CloudIcon
                    style={{
                        color:
                            theme.palette.mode === 'light' ? '#663299' : '#fff',
                    }}
                />
            ),
        },
        {
            key: 'flower',
            value: (
                <FilterVintageIcon
                    style={{
                        color:
                            theme.palette.mode === 'light' ? '#663299' : '#fff',
                    }}
                />
            ),
        },
    ];

    const options = {
        elevation: 0,
        search: false,
        filter: false,
        searchPlaceholder: 'Search by TERA ID',
        download: false,
        print: false,
        onRowClick: handleOpenWorkspace,
        // onTableChange: handleTableChange,
        count: (workspaceList && workspaceList.total) || 0,
        serverSide: false,
        rowsPerPage: 10,
        rowsPerPageOptions: [10],
        sortOrder: {
            // @ts-ignore
            direction: 'desc' as 'desc' | 'asc',
            name: 'createdAt',
        },
        viewColumns: false,
        selectableRowsHeader: false,
        selectableRowsHideCheckboxes: true,
        customToolbar: () => {
            return (
                <Grid container justifyContent={'flex-end'} spacing={2}>
                    <Grid item>
                        <Button
                            startIcon={<AddRoundedIcon />}
                            variant={'contained'}
                            size={'small'}
                            style={{
                                backgroundColor: '#663299',
                                color: '#fff',
                                fontWeight: 'bold',
                            }}
                            onClick={handleOpenDialog}
                        >
                            Create New Workspace
                        </Button>
                    </Grid>
                    <Grid item>
                        <Badge
                            showZero
                            badgeContent={
                                (pendingInvitesList &&
                                    pendingInvitesList.total) ||
                                0
                            }
                            color="secondary"
                        >
                            <Button
                                variant={'outlined'}
                                size={'small'}
                                onClick={() => setOpenPendingInvites(true)}
                            >
                                Pending Invitations
                            </Button>
                        </Badge>
                    </Grid>
                </Grid>
            );
        },
    };

    const loadingComponent = (
        <div
            style={{
                position: 'absolute',
                zIndex: 110,
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: `#fff80`,
            }}
        >
            <Loader size={60} color={'#60CEBD'} />
        </div>
    );
    return (
        <Grid container justifyContent={'center'} spacing={3}>
            <Grid item xs={12} sx={{ height: '100%', width: '100%' }}>
                <Paper elevation={0} sx={{ p: '0px 16px 0px 16px' }}>
                    <div
                        id="TableDiv"
                        style={{ position: 'relative', cursor: 'auto' }}
                    >
                        {workspaceListLoading && loadingComponent}
                        <MUIDataTable
                            data={workspaceList && workspaceList.items}
                            columns={columns}
                            title={
                                <Typography variant={'h6'}>
                                    My Workspaces
                                </Typography>
                            }
                            options={options}
                        />
                    </div>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default WorkspaceList;
